import { SchoolTime } from "./SchoolTime";
import * as types from "../gql/graphql";

// Add signup field to the event
//export interface StudentEvent extends types.Fs_Events {
//  signUpStatus: number; // 0 - full, 1 - available for signup, 2 - signed up, 3 - in wait list, 4 - attended
//}

// Unified generic CalendarTree
export class CalendarTree<T extends { id: number; date?: string }> {
  private years: Map<number, Map<number, Map<number, Map<number, T>>>>;

  constructor(orig: CalendarTree<T> = null) {
    if (orig) {
      this.years = new Map<number, Map<number, Map<number, Map<number, T>>>>(orig.years);
    } else {
      this.years = new Map<number, Map<number, Map<number, Map<number, T>>>>();
    }
  }

  addEvent(event: T): void {
    const date = SchoolTime.fromISO(event.date);
    const year = date.getYear();
    const month = date.getMonth();
    const day = date.getDay();

    if (!this.years.has(year)) {
      this.years.set(year, new Map<number, Map<number, Map<number, T>>>());
    }
    const yearMap = this.years.get(year)!;

    if (!yearMap.has(month)) {
      yearMap.set(month, new Map<number, Map<number, T>>());
    }
    const monthMap = yearMap.get(month)!;

    if (!monthMap.has(day)) {
      monthMap.set(day, new Map<number, T>());
    }
    const events = monthMap.get(day)!;

    events.set(event.id, event);
  }

  deleteEvent(event: T): void {
    const date = SchoolTime.fromISO(event.date);
    const year = date.getYear();
    const month = date.getMonth();
    const day = date.getDay();

    const yearMap = this.years.get(year);
    if (!yearMap) return;
    const monthMap = yearMap.get(month);
    if (!monthMap) return;
    const events = monthMap.get(day);
    if (!events) return;

    events.delete(event.id);
  }

  getEvents(year: number, month: number, day: number): T[] {
    if (!this.years.has(year)) {
      return [];
    }
    const yearMap = this.years.get(year)!;

    if (!yearMap.has(month)) {
      return [];
    }
    const monthMap = yearMap.get(month)!;

    if (!monthMap.has(day)) {
      return [];
    }

    const events = Array.from(monthMap.get(day)!.values());

    // Sort events by time if they have a time component in their date
    events.sort((a, b) => {
      const timeA = SchoolTime.fromISO(a.date).toDateTime();
      const timeB = SchoolTime.fromISO(b.date).toDateTime();
      return timeA.toMillis() - timeB.toMillis();
    });

    return events;
  }

  getEvent(year: number, month: number, day: number, id: number): T | null {
    if (!this.years.has(year)) {
      return null;
    }
    const yearMap = this.years.get(year)!;

    if (!yearMap.has(month)) {
      return null;
    }
    const monthMap = yearMap.get(month)!;

    if (!monthMap.has(day)) {
      return null;
    }

    const events = monthMap.get(day)!;
    return events.get(id) ?? null;
  }

  hasMonth(date: SchoolTime): boolean {
    const year = date.getYear();
    const month = date.getMonth();
    return this.years.has(year) && this.years.get(year)!.has(month);
  }
}

// Usage examples:
// For Fs_Events: const tree = new CalendarTree<types.Fs_Events>();
// For StudentEvent: const tree = new CalendarTree<StudentEvent>();